import type { UploadUrlResponse } from '@saaga/shared/interfaces/file.interface'
import { logger } from '@saaga/shared/utils/logger.util'

export interface FileUploadResult {
  fileId: string
  filename: string
  mimeType: string
  sizeBytes: number
}

export interface FileUploadCallbacks {
  requestUploadUrl: (params: { filename: string; mime_type: string; size_bytes: number }) => {
    unwrap: () => Promise<UploadUrlResponse>
  }
  confirmUpload: (fileId: string) => { unwrap: () => Promise<unknown> }
  onProgress?: (fileId: string, progress: number) => void
}

export const uploadFileToS3 = async (
  file: File,
  callbacks: FileUploadCallbacks
): Promise<FileUploadResult> => {
  const urlResponse = await requestPresignedUrl(file, callbacks)
  await putFileToS3(file, urlResponse, callbacks)
  await confirmFileUpload(urlResponse.file_id, callbacks)

  return {
    fileId: urlResponse.file_id,
    filename: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  }
}

export const uploadMultipleFiles = async (
  fileList: File[],
  callbacks: FileUploadCallbacks
): Promise<FileUploadResult[]> => {
  const resultList = await Promise.all(fileList.map((file) => uploadFileToS3(file, callbacks)))
  return resultList
}

const requestPresignedUrl = async (
  file: File,
  callbacks: FileUploadCallbacks
): Promise<UploadUrlResponse> => {
  logger.debug(`Requesting upload URL for ${file.name}`)
  return callbacks
    .requestUploadUrl({
      filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size_bytes: file.size,
    })
    .unwrap()
}

const putFileToS3 = async (
  file: File,
  urlResponse: UploadUrlResponse,
  callbacks: FileUploadCallbacks
): Promise<void> => {
  logger.debug(`Uploading ${file.name} to S3`)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', urlResponse.upload_url)
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return
      const progress = Math.round((event.loaded / event.total) * 100)
      callbacks.onProgress?.(urlResponse.file_id, progress)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        callbacks.onProgress?.(urlResponse.file_id, 100)
        resolve()
        return
      }
      reject(new Error(`S3 upload failed with status ${xhr.status}`))
    })

    xhr.addEventListener('error', () => {
      reject(new Error('S3 upload network error'))
    })

    xhr.send(file)
  })
}

const confirmFileUpload = async (fileId: string, callbacks: FileUploadCallbacks): Promise<void> => {
  logger.debug(`Confirming upload for file ${fileId}`)
  await callbacks.confirmUpload(fileId).unwrap()
}
